// KES formatting + client-side calculation mirror of backend calc.py

export function kes(v, dp = 2) {
  const n = Number(v || 0);
  return n.toLocaleString("en-KE", { minimumFractionDigits: dp, maximumFractionDigits: dp });
}

export function ltr(v, dp = 2) {
  const n = Number(v || 0);
  return n.toLocaleString("en-KE", { minimumFractionDigits: dp, maximumFractionDigits: dp });
}

const r = (v, n = 3) => {
  const f = Number(v || 0);
  if (Number.isNaN(f)) return 0;
  const p = Math.pow(10, n);
  return Math.round(f * p) / p;
};

export const STATUS_LABELS = {
  draft: "Draft",
  submitted: "Submitted",
  manager_reviewed: "Manager Reviewed",
  accountant_approved: "Accountant Approved",
  locked: "Locked",
};

// shift: arrays (fuel_readings, tank_dips, nonfuel, payments, expenses, fuel_prices, actual_cash)
// config: {station, pumps, tanks, products, nonfuel_items, payment_methods}
export function computeShift(shift, config) {
  const station = config.station || {};
  const cashTol = Number(station.cash_tolerance ?? 100);
  const warnPct = Number(station.throughput_warn_pct ?? 0.2);
  const critPct = Number(station.throughput_crit_pct ?? 0.5);

  const pumps = Object.fromEntries((config.pumps || []).map((p) => [String(p.id), p]));
  const tanks = Object.fromEntries((config.tanks || []).map((t) => [String(t.id), t]));
  const items = Object.fromEntries((config.nonfuel_items || []).map((i) => [String(i.id), i]));
  const methods = Object.fromEntries((config.payment_methods || []).map((m) => [String(m.id), m]));
  const fuelPrices = shift.fuel_prices || {};
  const fuelCostPrices = shift.fuel_cost_prices || {};

  // Manual = physical meter reading; Electronic = pump's own totalizers (liters + cumulative KES) —
  // Electronic is authoritative for sales/throughput, matching the station's reconciliation sheet.
  const fuel_lines = [];
  let fuel_sales_total = 0;
  let fuel_cost_total = 0;
  const liters_by_tank = {};
  const test_by_tank = {};
  (shift.fuel_readings || []).forEach((row) => {
    const pump = pumps[String(row.pump_id)] || {};
    const product_id = String(row.product_id || pump.product_id || "");
    const opening = r(row.opening);
    const closing = r(row.closing);
    const testing = r(row.testing);
    const price = r(fuelPrices[product_id] || 0, 4);
    const cost_price = r(fuelCostPrices[product_id] || 0, 4);

    const manual_liters_sold = r(closing - opening - testing);
    const manual_amount = r(manual_liters_sold * price, 2);

    const elec_opening_l = r(row.elec_opening_l);
    const elec_closing_l = r(row.elec_closing_l);
    const elec_opening_kes = r(row.elec_opening_kes, 2);
    const elec_closing_kes = r(row.elec_closing_kes, 2);
    const electronic_liters_sold = r(elec_closing_l - elec_opening_l - testing);
    const electronic_amount = r(elec_closing_kes - elec_opening_kes, 2);

    const has_electronic = elec_opening_l !== 0 || elec_closing_l !== 0;
    const liters_sold = has_electronic ? electronic_liters_sold : manual_liters_sold;
    const amount = has_electronic ? electronic_amount : manual_amount;
    const cost = r(liters_sold * cost_price, 2);
    const gross_profit = r(amount - cost, 2);

    fuel_sales_total += amount;
    fuel_cost_total += cost;
    const tank_id = String(pump.tank_id || "");
    if (tank_id) {
      liters_by_tank[tank_id] = r((liters_by_tank[tank_id] || 0) + liters_sold);
      test_by_tank[tank_id] = r((test_by_tank[tank_id] || 0) + testing, 2);
    }
    fuel_lines.push({
      pump_id: String(row.pump_id), product_id, opening, closing, testing,
      manual_liters_sold, manual_amount, elec_opening_l, elec_closing_l, elec_opening_kes, elec_closing_kes,
      electronic_liters_sold, electronic_amount,
      variance_liters: r(electronic_liters_sold - manual_liters_sold, 3),
      variance_amount: r(electronic_amount - manual_amount, 2),
      liters_sold, unit_price: price, amount, cost_price, cost, gross_profit,
    });
  });

  // ----- Deliveries (itemized: invoice details + offloaded vs invoiced volume) — see calc.py -----
  const tank_delivery_lines = [];
  const deliveriesByTank = {};
  const tanksWithDeliveries = new Set();
  (shift.tank_deliveries || []).forEach((row) => {
    const tank_id = String(row.tank_id);
    tanksWithDeliveries.add(tank_id);
    const invoice_volume = r(row.invoice_volume, 2);
    const offloaded_quantity = r(row.offloaded_quantity, 2);
    const variance = r(offloaded_quantity - invoice_volume, 2);
    const pct = invoice_volume ? r((Math.abs(variance) / invoice_volume) * 100, 3) : 0;
    const status = pct <= warnPct ? "within" : (pct <= critPct ? "warning" : "critical");
    deliveriesByTank[tank_id] = r((deliveriesByTank[tank_id] || 0) + offloaded_quantity, 2);
    tank_delivery_lines.push({
      tank_id, invoice_no: row.invoice_no || "", date: row.date || "",
      product_id: String(row.product_id || tanks[tank_id]?.product_id || ""),
      invoice_amount: r(row.invoice_amount, 2), invoice_volume, offloaded_quantity,
      variance, variance_pct: pct, status,
    });
  });

  const tank_lines = [];
  let total_tank_variance_l = 0;
  (shift.tank_dips || []).forEach((row) => {
    const tank = tanks[String(row.tank_id)] || {};
    const opening_dip = r(row.opening_dip);
    const deliveries = tanksWithDeliveries.has(String(row.tank_id)) ? r(deliveriesByTank[String(row.tank_id)], 2) : r(row.deliveries);
    const rtt = r(row.rtt);
    const closing_dip = r(row.closing_dip);
    const dip_sales = r(opening_dip + deliveries + rtt - closing_dip);
    const pump_throughput = r(liters_by_tank[String(row.tank_id)] || 0);
    const pump_test = r(test_by_tank[String(row.tank_id)] || 0, 2);
    const variance_l = r(pump_throughput - dip_sales);
    const pct = pump_throughput ? r((Math.abs(variance_l) / pump_throughput) * 100, 3) : 0;
    let status = "within";
    if (pct > critPct) status = "critical";
    else if (pct > warnPct) status = "warning";
    total_tank_variance_l += variance_l;
    tank_lines.push({ tank_id: String(row.tank_id), product_id: String(tank.product_id || ""), opening_dip, deliveries, rtt, pump_test, closing_dip, dip_sales, pump_throughput, variance_l, variance_pct: pct, status });
  });

  // ----- Non-fuel deliveries (itemized: invoice details + received vs invoiced quantity) — see calc.py -----
  const nonfuel_delivery_lines = [];
  const receivedByItem = {};
  const itemsWithDeliveries = new Set();
  (shift.nonfuel_deliveries || []).forEach((row) => {
    const item_id = String(row.item_id);
    itemsWithDeliveries.add(item_id);
    const invoice_quantity = r(row.invoice_quantity, 2);
    const actual_quantity = r(row.actual_quantity, 2);
    const variance = r(actual_quantity - invoice_quantity, 2);
    const pct = invoice_quantity ? r((Math.abs(variance) / invoice_quantity) * 100, 3) : 0;
    const status = pct <= warnPct ? "within" : (pct <= critPct ? "warning" : "critical");
    receivedByItem[item_id] = r((receivedByItem[item_id] || 0) + actual_quantity, 2);
    nonfuel_delivery_lines.push({
      item_id, invoice_no: row.invoice_no || "", date: row.date || "",
      invoice_amount: r(row.invoice_amount, 2), invoice_quantity, actual_quantity,
      variance, variance_pct: pct, status,
    });
  });

  const groupFor = (category) => (category === "lube" ? "lube" : "lpg_accessory");
  const nonfuel_lines = [];
  let nonfuel_revenue_total = 0;
  let nonfuel_cost_total = 0;
  const nonfuel_revenue_by_group = { lube: 0, lpg_accessory: 0 };
  (shift.nonfuel || []).forEach((row) => {
    const item = items[String(row.item_id)] || {};
    const item_id = String(row.item_id);
    const opening = r(row.opening);
    const received = itemsWithDeliveries.has(item_id) ? r(receivedByItem[item_id], 2) : r(row.received);
    const closing = r(row.closing);
    const sold = r(opening + received - closing);
    const unit_price = r(row.unit_price ?? item.unit_price ?? 0, 2);
    const cost_price = r(row.cost_price ?? item.cost_price ?? 0, 2);
    const revenue = r(sold * unit_price, 2);
    const cost = r(sold * cost_price, 2);
    nonfuel_revenue_total += revenue;
    nonfuel_cost_total += cost;
    const group = groupFor(item.category);
    nonfuel_revenue_by_group[group] = r(nonfuel_revenue_by_group[group] + revenue, 2);
    nonfuel_lines.push({ item_id: String(row.item_id), opening, received, closing, sold, unit_price, cost_price, revenue, cost, gross_profit: r(revenue - cost, 2), category: item.category });
  });

  // ----- Non-fuel payments (per group: Lubes / LPG & Accessories — full payment-method
  // breakdown, same as fuel Section D, since non-fuel sales aren't all cash either) -----
  const nonfuelGroupAttendantMap = {};
  Object.entries(shift.nonfuel_group_attendants || {}).forEach(([k, v]) => { if (v) nonfuelGroupAttendantMap[k] = String(v); });
  const nonfuel_payment_lines = [];
  const nonfuelDeclaredByGroup = { lube: 0, lpg_accessory: 0 };
  (shift.nonfuel_payments || []).forEach((row) => {
    if (row.group !== "lube" && row.group !== "lpg_accessory") return;
    const method = methods[String(row.method_id)] || {};
    const amount = r(row.amount, 2);
    const kind = method.kind || "other";
    const method_id = String(row.method_id);
    nonfuelDeclaredByGroup[row.group] = r(nonfuelDeclaredByGroup[row.group] + amount, 2);
    nonfuel_payment_lines.push({
      group: row.group, attendant_id: nonfuelGroupAttendantMap[row.group] || null,
      method_id, amount, kind, reference: row.reference || "", till_id: row.till_id || null,
    });
  });

  const payment_lines = [];
  let payments_total = 0;
  let non_cash_total = 0;
  let cash_declared = 0;
  const payments_by_method = {};
  (shift.payments || []).forEach((row) => {
    const method = methods[String(row.method_id)] || {};
    const amount = r(row.amount, 2);
    const kind = method.kind || "other";
    const attendant_id = row.attendant_id || null;
    payments_total += amount;
    if (kind === "cash") cash_declared += amount;
    else non_cash_total += amount;
    const method_id = String(row.method_id);
    payments_by_method[method_id] = r((payments_by_method[method_id] || 0) + amount, 2);
    payment_lines.push({ attendant_id, method_id, amount, kind, reference: row.reference || "" });
  });

  // Non-fuel payments feed into the SAME shift-wide cash/non-cash totals as fuel payments.
  nonfuel_payment_lines.forEach((line) => {
    payments_total = r(payments_total + line.amount, 2);
    if (line.kind === "cash") cash_declared = r(cash_declared + line.amount, 2);
    else non_cash_total = r(non_cash_total + line.amount, 2);
    payments_by_method[line.method_id] = r((payments_by_method[line.method_id] || 0) + line.amount, 2);
  });

  const nonfuel_group_lines = ["lube", "lpg_accessory"].map((group) => {
    const attendant_id = nonfuelGroupAttendantMap[group] || "";
    const expected_revenue = r(nonfuel_revenue_by_group[group] || 0, 2);
    const declared_amount = r(nonfuelDeclaredByGroup[group] || 0, 2);
    const variance = r(declared_amount - expected_revenue, 2);
    const status = Math.abs(variance) <= cashTol ? "within" : (Math.abs(variance) <= cashTol * 5 ? "warning" : "critical");
    return { group, attendant_id: attendant_id || null, expected_revenue, declared_amount, variance, status };
  });

  // ----- Per-attendant reconciliation (fuel sales assigned to them vs what they collected) -----
  const pumpAttendantMap = {};
  Object.entries(shift.pump_attendants || {}).forEach(([k, v]) => { if (v) pumpAttendantMap[String(k)] = String(v); });
  const attendantSales = {};
  fuel_lines.forEach((line) => {
    const att = pumpAttendantMap[line.pump_id] || "";
    attendantSales[att] = r((attendantSales[att] || 0) + line.amount, 2);
  });
  const attendantPayments = {};
  const attendantCollected = {};
  const attendantCash = {};
  const attendantNonCash = {};
  payment_lines.forEach((line) => {
    const att = line.attendant_id || "";
    attendantPayments[att] = attendantPayments[att] || {};
    attendantPayments[att][line.method_id] = r((attendantPayments[att][line.method_id] || 0) + line.amount, 2);
    attendantCollected[att] = r((attendantCollected[att] || 0) + line.amount, 2);
    if (line.kind === "cash") attendantCash[att] = r((attendantCash[att] || 0) + line.amount, 2);
    else attendantNonCash[att] = r((attendantNonCash[att] || 0) + line.amount, 2);
  });
  const attendantIds = new Set([...Object.keys(attendantSales), ...Object.keys(attendantCollected)]);
  const attendant_lines = [...attendantIds].map((att_id) => {
    const sales = r(attendantSales[att_id] || 0, 2);
    const collected = r(attendantCollected[att_id] || 0, 2);
    const variance = r(collected - sales, 2);
    const status = Math.abs(variance) <= cashTol ? "within" : (Math.abs(variance) <= cashTol * 5 ? "warning" : "critical");
    return {
      attendant_id: att_id || null,
      assigned_fuel_sales: sales,
      payments_by_method: attendantPayments[att_id] || {},
      cash_collected: r(attendantCash[att_id] || 0, 2),
      non_cash_collected: r(attendantNonCash[att_id] || 0, 2),
      collected_total: collected,
      variance, status,
    };
  }).sort((a, b) => (a.attendant_id === null) - (b.attendant_id === null) || String(a.attendant_id).localeCompare(String(b.attendant_id)));

  // ----- Itemized transactions (optional detail to match against each payment lump sum) -----
  const lumpByGroup = {};
  payment_lines.forEach((line) => {
    const key = `${line.attendant_id || ""}|${line.method_id}`;
    lumpByGroup[key] = r((lumpByGroup[key] || 0) + line.amount, 2);
  });
  const txnByGroup = {};
  (shift.payment_transactions || []).forEach((t) => {
    const key = `${t.attendant_id || ""}|${t.method_id}`;
    (txnByGroup[key] = txnByGroup[key] || []).push({ amount: r(t.amount, 2), reference: t.reference || "", note: t.note || "" });
  });
  const groupKeys = new Set([...Object.keys(lumpByGroup), ...Object.keys(txnByGroup)]);
  const payment_itemization = [...groupKeys].map((key) => {
    const [attendant_id, method_id] = key.split("|");
    const txns = txnByGroup[key] || [];
    const itemized_total = r(txns.reduce((a, t) => a + t.amount, 0), 2);
    const lump_amount = r(lumpByGroup[key] || 0, 2);
    return { attendant_id: attendant_id || null, method_id, lump_amount, itemized_total, variance: r(itemized_total - lump_amount, 2), transactions: txns };
  }).sort((a, b) => (a.attendant_id === null) - (b.attendant_id === null) || String(a.attendant_id).localeCompare(String(b.attendant_id)) || a.method_id.localeCompare(b.method_id));

  // ----- M-Pesa till reconciliation (see calc.py for the full explanation) -----
  const tills = Object.fromEntries((config.tills || []).map((t) => [String(t.id), t]));
  const tillAttendantMap = {};
  // several attendants can share one till: a list (older shifts stored a single attendant)
  Object.entries(shift.till_attendants || {}).forEach(([k, v]) => {
    if (v) tillAttendantMap[String(k)] = [...new Set((Array.isArray(v) ? v : [v]).filter(Boolean).map(String))];
  });
  const attendantMpesa = {};
  payment_lines.forEach((line) => {
    if (line.kind === "mobile_money") {
      const att = line.attendant_id || "";
      attendantMpesa[att] = r((attendantMpesa[att] || 0) + line.amount, 2);
    }
  });
  const tillMpesaDirect = {};
  nonfuel_payment_lines.forEach((line) => {
    if (line.kind === "mobile_money" && line.till_id) {
      tillMpesaDirect[line.till_id] = r((tillMpesaDirect[line.till_id] || 0) + line.amount, 2);
    }
  });
  // Account payments received: customers settling their accounts and Rubis Card top-ups (see calc.py) -
  // M-Pesa into a till's expected balance, cash added to the cash to bank; never sales
  const account_receipt_lines = [];
  const accountTillIn = {};
  let account_cash_received = 0;
  const account_receipts_by_method = {};
  (shift.account_receipts || []).forEach((a) => {
    const amount = r(a.amount, 2);
    if (!amount) return;
    const method_id = String(a.method_id || "");
    const kind = method_id === "cheque" || method_id === "transfer" ? method_id : (methods[method_id] || {}).kind || "other";
    const till_id = kind === "mobile_money" ? String(a.till_id || "") : "";
    if (till_id) accountTillIn[till_id] = r((accountTillIn[till_id] || 0) + amount, 2);
    if (kind === "cash") account_cash_received = r(account_cash_received + amount, 2);
    account_receipts_by_method[method_id] = r((account_receipts_by_method[method_id] || 0) + amount, 2);
    account_receipt_lines.push({ _k: a._k, account_id: a.account_id ? String(a.account_id) : null, method_id: method_id || null,
      kind, till_id: till_id || null, amount, reference: (a.reference || "").trim(), note: (a.note || "").trim(),
      bank: kind === "cheque" || kind === "transfer" ? (a.bank || "").trim() : "" });
  });
  const till_lines = (shift.till_ledger || []).map((row) => {
    const till_id = String(row.till_id);
    const till = tills[till_id] || {};
    const attendant_ids = tillAttendantMap[till_id] || [];
    const attendant_id = attendant_ids[0] || "";
    const opening_balance = r(row.opening_balance, 2);
    const withdrawal = r(row.withdrawal, 2);
    const account_in = accountTillIn[till_id] || 0;
    const mpesa_received = r(attendant_ids.reduce((a, id) => a + (attendantMpesa[id] || 0), 0) + (tillMpesaDirect[till_id] || 0) + account_in, 2);
    const closing_balance = r(opening_balance + mpesa_received - withdrawal, 2);

    const rawActual = row.actual_balance;
    const actual_balance = rawActual !== null && rawActual !== undefined && rawActual !== "" ? r(rawActual, 2) : null;
    const variance = actual_balance !== null ? r(actual_balance - closing_balance, 2) : null;
    let status = null;
    if (variance !== null) {
      status = Math.abs(variance) <= cashTol ? "within" : (Math.abs(variance) <= cashTol * 5 ? "warning" : "critical");
    }

    return {
      till_id, till_label: till.label || "", till_number: till.till_number || "",
      attendant_id: attendant_id || null, opening_balance, mpesa_received, withdrawal, closing_balance,
      actual_balance, variance, status,
    };
  });

  let expenses_total = 0;
  (shift.expenses || []).forEach((e) => (expenses_total += r(e.amount, 2)));

  const total_sales = r(fuel_sales_total + nonfuel_revenue_total, 2);
  const expected_cash = r(total_sales - non_cash_total - expenses_total, 2);
  const actual_cash = r(cash_declared, 2); // sum of "Cash" entries across all attendants
  const cash_variance = r(actual_cash - expected_cash, 2);
  let cash_status = "within";
  if (Math.abs(cash_variance) > cashTol * 5) cash_status = "critical";
  else if (Math.abs(cash_variance) > cashTol) cash_status = "warning";

  return {
    account_receipt_lines,
    account_receipt_hints: till_lines.filter((l) => l.variance !== null && l.variance > cashTol).map((l) => ({ till_id: l.till_id, amount: l.variance })),
    fuel_lines, tank_lines, tank_delivery_lines, nonfuel_delivery_lines, nonfuel_lines, nonfuel_group_lines, nonfuel_payment_lines, payment_lines, attendant_lines, payment_itemization, till_lines,
    totals: {
      fuel_sales_total: r(fuel_sales_total, 2),
      fuel_cost_total: r(fuel_cost_total, 2),
      fuel_gross_total: r(fuel_sales_total - fuel_cost_total, 2),
      nonfuel_revenue_total: r(nonfuel_revenue_total, 2),
      nonfuel_revenue_by_group,
      nonfuel_cost_total: r(nonfuel_cost_total, 2),
      nonfuel_gross_total: r(nonfuel_revenue_total - nonfuel_cost_total, 2),
      payments_total: r(payments_total, 2),
      payments_by_method,
      non_cash_total: r(non_cash_total, 2),
      cash_declared: r(cash_declared, 2),
      expenses_total: r(expenses_total, 2),
      total_sales, expected_cash, actual_cash, cash_variance, cash_status,
      account_receipts_total: r(account_receipt_lines.reduce((x, l) => x + l.amount, 0), 2), account_receipts_by_method,
      // Rubis Card top-ups: account payments against the Rubis Card account
      topups_total: r(account_receipt_lines.filter((l) => (config.receivable_accounts || []).some((a) => String(a.id) === l.account_id && a.kind === "card" && /rubis/i.test(a.name || ""))).reduce((x, l) => x + l.amount, 0), 2),
      account_cash_received, cash_to_bank: r(actual_cash + account_cash_received, 2),
      total_tank_variance_l: r(total_tank_variance_l),
    },
  };
}
